import { API_ENDPOINTS } from "../config/constants";
import { getStoredSession } from "./impersonation";
import { isFullOfflineModeEnabled } from "./fullOfflineMode";
import {
  getOfflineLoyaltySettings,
  saveOfflineLoyaltySettings,
} from "../offline/loyaltyStore";

const authHeaders = () => {
  const session = getStoredSession();
  const token = session?.access_token || session?.token || session?.data?.access_token || session?.data?.token;
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const withRestaurantId = (url, restaurantId) => {
  const separator = String(url).includes("?") ? "&" : "?";
  return `${url}${separator}restaurant_id=${encodeURIComponent(restaurantId)}`;
};

export const loyaltyRequest = async (url, options = {}) => {
  const response = await fetch(url, { ...options, headers: { ...authHeaders(), ...(options.headers || {}) } });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || !json?.success) throw new Error(json?.message || "Unable to load loyalty data.");
  return json;
};

export const getLoyaltySettings = (restaurantId) =>
  isFullOfflineModeEnabled()
    ? getOfflineLoyaltySettings(restaurantId)
    : loyaltyRequest(withRestaurantId(API_ENDPOINTS.LOYALTY_SETTINGS, restaurantId));

export const saveLoyaltySettings = (restaurantId, payload) =>
  isFullOfflineModeEnabled()
    ? saveOfflineLoyaltySettings(restaurantId, payload)
    : loyaltyRequest(API_ENDPOINTS.LOYALTY_SETTINGS, {
        method: "PUT",
        body: JSON.stringify({ ...payload, restaurant_id: Number(restaurantId) }),
      });

export const getCustomerLoyalty = (restaurantId, partyId) =>
  loyaltyRequest(withRestaurantId(API_ENDPOINTS.LOYALTY_CUSTOMER(partyId), restaurantId));

export const getLoyaltyCustomerList = (restaurantId) =>
  loyaltyRequest(withRestaurantId(API_ENDPOINTS.LOYALTY_CUSTOMERS, restaurantId));

export const getLoyaltyRedemptionPreview = (restaurantId, partyId, billAmount) =>
  loyaltyRequest(API_ENDPOINTS.LOYALTY_REDEMPTION_PREVIEW, {
    method: "POST",
    body: JSON.stringify({
      restaurant_id: Number(restaurantId),
      party_id: Number(partyId),
      bill_amount: Number(billAmount),
    }),
  });

export const resolveUsedLoyaltyPoints = (...sources) => {
  const redemption = sources.find(
    (source) => source && typeof source === "object"
  );
  if (!redemption) return 0;

  const type = String(
    redemption?.type ?? redemption?.reward_type ?? ""
  ).toLowerCase();
  if (type && type !== "points") return 0;

  const points = Number(
    redemption?.points_used ??
      redemption?.pointsUsed ??
      redemption?.loyalty_points_used ??
      0
  );
  if (!Number.isFinite(points) || points <= 0) return 0;

  return Math.round(points * 100) / 100;
};
